import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { supabase, GOOGLE_OAUTH_ENABLED } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { buildGoogleOAuthArgs } from "../lib/googleAuth";
import { toast } from "sonner";
import {
  EnvelopeSimple, ArrowRight, Calculator, MagnifyingGlass,
  Lightning, Package, AirplaneInFlight, ShieldCheck, Truck, Camera,
} from "@phosphor-icons/react";
import GoogleGIcon from "../components/GoogleGIcon";
import PublicHeader from "../components/public/PublicHeader";
import PublicFooter from "../components/public/PublicFooter";
import CheckPriceDrawer from "../components/public/CheckPriceDrawer";
import PriorityAccessPanel from "../components/public/PriorityAccessPanel";
import { pushEvent, currentPagePath } from "../lib/analytics";

const HERO_IMG =
  "https://images.unsplash.com/photo-1670926774123-4e36ed8f5fd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwY2FyZ28lMjBwbGFuZSUyMGFpcnBvcnQlMjBuaWdodHxlbnwwfHx8fDE3ODAzMTE4Mzl8MA&ixlib=rb-4.1.0&q=85&w=1400&h=1100";

const STEPS = [
  { icon: Calculator, title: "Check Price", body: "Use the instant estimator to see indicative pricing." },
  { icon: Package, title: "Submit Shipment", body: "Add invoice, package details and receiver information." },
  { icon: AirplaneInFlight, title: "Move by Air", body: "After clearance and payment, Air12 attempts movement on the next suitable flight." },
];

const TRUST = ["Verified Handling", "Instant Estimate", "Mandatory Insurance", "Secure Tracking"];

const BENEFITS = [
  { icon: Lightning, title: "Pickup within 60 minutes*", body: "Fast pickup for time-sensitive business cargo." },
  { icon: AirplaneInFlight, title: "Door-to-door in under 12 hours**", body: "Priority air movement from pickup to final delivery." },
  { icon: Truck, title: "Dedicated pickup & delivery vehicles", body: "Direct local movement at both ends." },
  { icon: Camera, title: "Timestamped photo evidence", body: "Visual proof captured at key custody milestones." },
];

export default function PublicEntry({ interim = false }) {
  const { session, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  // M3 — passwordless email OTP (6-digit) Sender login. Staff email/password login now lives on the
  // dedicated /staff/login page; this public panel is Sender-only (Google + email OTP).
  const [authView, setAuthView] = useState("default"); // "default" | "otp"
  const [otpStep, setOtpStep] = useState("email");      // "email" | "code"
  const [otpCode, setOtpCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // RC-2: honour a post-login redirect target (e.g. a /scan/pkg/:token QR deep-link).
  const rawRedirect = new URLSearchParams(window.location.search).get("redirect");
  const redirectTo = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
    ? rawRedirect
    : "/app";

  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#check-price") setPriceOpen(true);
    if (location.hash === "#signin") {
      document.getElementById("signin")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (loading) return <div className="p-10 mono text-sm">Loading…</div>;
  if (session) return <Navigate to={redirectTo} replace />;

  const onGoogle = async () => {
    pushEvent("login_click_google", { page_path: currentPagePath(), placement: "login", cta_name: "login_google", auth_method: "google" });
    setBusy(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    // Only an internal relative path (already validated to start with a single "/") is appended.
    // AUTH-GOOGLE-01-B: request Google's account chooser every click (prompt=select_account).
    const { error } = await supabase.auth.signInWithOAuth(
      buildGoogleOAuthArgs(window.location.origin, redirectTo)
    );
    if (error) { toast.error("Something went wrong. Please try again."); setBusy(false); }
  };

  const backToDefault = () => { setAuthView("default"); setOtpStep("email"); setOtpCode(""); setCooldown(0); };

  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const sendOtp = async () => {
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    // Passwordless 6-digit code. Non-enumeration: the same call is made whether or not the email
    // already exists, so the response never reveals prior registration.
    const { error } = await supabase.auth.signInWithOtp({
      email: normalized, options: { shouldCreateUser: true },
    });
    setBusy(false);
    if (error) { toast.error("We couldn’t send the code. Please try again."); return; }
    setEmail(normalized);
    setOtpStep("code");
    setOtpCode("");
    setCooldown(60);
    toast.success("We sent a 6-digit code to your email.");
  };

  const resendOtp = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(), options: { shouldCreateUser: true },
    });
    setBusy(false);
    if (error) { toast.error("We couldn't resend the code. Please try again."); return; }
    setCooldown(60);
    toast.success("A new code was sent.");
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    const code = otpCode.replace(/\D/g, "");
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(), token: code, type: "email",
    });
    setBusy(false);
    if (error) {
      // Safe generic message; never reveal whether the email was previously registered.
      toast.error("Invalid or expired code. Request a new code and try again.");
      setOtpCode("");
      return;
    }
    // On success the Supabase session is set via onAuthStateChange; the <Navigate> above routes
    // the user to /app -> role/onboarding routing.
  };

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(var(--background))]" data-testid="login-page">
      <PublicHeader />

      {/* Hero split */}
      <section className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left — dark cargo hero */}
        <div
          className="relative lg:col-span-7 min-h-[460px] lg:min-h-[calc(100vh-64px)] flex flex-col justify-between p-8 lg:p-16 text-white"
          data-testid="hero-panel"
        >
          <div className="absolute inset-0">
            <img src={HERO_IMG} alt="Air cargo aircraft on the tarmac" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/65" />
          </div>

          <div className="relative">
            <div className="overline text-white/70 mb-4">PRIORITY ROUTES · BOM ↔ DEL · BOM ↔ BLR</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl heading leading-[1.02] tracking-tight max-w-2xl">
              Priority air cargo. Made affordable.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">
              Instantly estimate urgent business cargo movement by air on Air12 Priority Routes.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                className="inline-flex items-center gap-2 h-11 px-5 bg-white text-[hsl(var(--foreground))] font-semibold text-sm rounded-[2px] hover:bg-white/90 transition-colors"
                onClick={() => { pushEvent("check_price_click", { page_path: currentPagePath(), placement: "hero", cta_name: "check_price" }); setPriceOpen(true); }}
                data-testid="hero-check-price-btn"
              >
                <Calculator size={16} weight="bold" /> Check Price
              </button>
              <Link
                to="/track-my-shipment"
                className="inline-flex items-center gap-2 h-11 px-5 border border-white/40 text-white font-semibold text-sm rounded-[2px] hover:bg-white/10 transition-colors"
                data-testid="hero-track-shipment-btn"
                onClick={() => pushEvent("track_shipment_click", { page_path: currentPagePath(), placement: "hero", cta_name: "track_shipment" })}
              >
                <MagnifyingGlass size={16} weight="bold" /> Track Shipment
              </Link>
            </div>
          </div>

          <div className="relative mt-10 lg:mt-0">
            <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-white/60">
              {TRUST.map((t, i) => (
                <span key={t} className="flex items-center gap-4">
                  {i > 0 && <span className="text-white/30">·</span>}{t}
                </span>
              ))}
            </div>
            <p className="text-xs text-white/55 leading-relaxed mt-4 max-w-xl">
              Final booking is subject to document clearance, cargo acceptance, mandatory insurance,
              payment confirmation and flight availability.
            </p>
          </div>
        </div>

        {/* Right — sign in */}
        <div className="lg:col-span-5 bg-[hsl(var(--surface))] lg:border-l flex items-center justify-center p-8 lg:p-14" id="signin">
          {interim ? (
            <PriorityAccessPanel />
          ) : (
          <div className="w-full max-w-sm">
            <div className="overline mb-2">SIGN IN</div>
            <h2 className="heading text-2xl mb-1 tracking-tight">Welcome to Air12</h2>
            <p className="text-sm text-[hsl(var(--muted))] mb-7">
              Senders: continue with Google or email to submit shipments and view confirmed pricing.
            </p>

            {authView === "otp" ? (
              <div data-testid="otp-panel">
                {otpStep === "email" ? (
                  <form onSubmit={(e) => { e.preventDefault(); sendOtp(); }} className="space-y-3" data-testid="otp-email-form">
                    <label className="block">
                      <div className="overline mb-1.5">EMAIL</div>
                      <input type="email" className="input-field" value={email}
                             onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                             placeholder="you@company.com" data-testid="otp-email-input" />
                    </label>
                    <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="otp-send-code-button">
                      <EnvelopeSimple size={14} weight="bold" /> {busy ? "Sending…" : "Send code"}
                      <ArrowRight size={14} weight="bold" />
                    </button>
                    <p className="text-xs text-[hsl(var(--muted))] leading-relaxed">
                      We’ll email you a 6-digit code. No password needed.
                    </p>
                  </form>
                ) : (
                  <form onSubmit={verifyOtp} className="space-y-3" data-testid="otp-code-form">
                    <p className="text-sm text-[hsl(var(--muted))]">
                      Enter the 6-digit code sent to <span className="font-semibold text-[hsl(var(--foreground))]">{email}</span>
                    </p>
                    <label className="block">
                      <div className="overline mb-1.5">6-DIGIT CODE</div>
                      <input inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                             className="input-field tracking-[0.4em] text-center text-lg" value={otpCode}
                             onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                             required data-testid="otp-code-input" />
                    </label>
                    <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="otp-verify-button">
                      {busy ? "Verifying…" : "Verify & continue"} <ArrowRight size={14} weight="bold" />
                    </button>
                    <button type="button" className="btn-ghost w-full justify-center" disabled={busy || cooldown > 0}
                            onClick={resendOtp} data-testid="otp-resend-button">
                      {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                    </button>
                    <button type="button" className="text-xs underline text-[hsl(var(--muted))]"
                            onClick={() => { setOtpStep("email"); setOtpCode(""); }} data-testid="otp-change-email-button">
                      Use another email
                    </button>
                  </form>
                )}
                <div className="mt-6 text-xs text-[hsl(var(--muted))]">
                  <button onClick={backToDefault} className="underline font-semibold" data-testid="otp-back-button">
                    Back to sign-in options
                  </button>
                </div>
              </div>
            ) : (
              <>
                {GOOGLE_OAUTH_ENABLED && (
                  <button type="button" className="btn-ghost w-full justify-center mb-3 gap-2" onClick={onGoogle} disabled={busy} data-testid="login-google-button">
                    <GoogleGIcon className="w-4 h-4" /> Continue with Google
                  </button>
                )}

                <button type="button" className="btn-ghost w-full justify-center gap-2"
                        onClick={() => { pushEvent("login_click_email", { page_path: currentPagePath(), placement: "login", cta_name: "login_email", auth_method: "email" }); setAuthView("otp"); setOtpStep("email"); }} disabled={busy} data-testid="login-email-otp-button">
                  <EnvelopeSimple size={16} weight="bold" /> Continue with email
                </button>

                <p className="text-xs text-[hsl(var(--muted))] leading-relaxed mt-4">
                  No password needed — continue with Google or a one-time email code to submit shipments
                  and view confirmed pricing.
                </p>
              </>
            )}
            <div className="overline mt-9 text-[10px]">SENDERS · GOOGLE OR EMAIL · NO PASSWORD REQUIRED</div>
          </div>
          )}
        </div>
      </section>

      {/* How Air12 works */}
      <section className="bg-[hsl(var(--background))] py-14 md:py-20 px-5 md:px-10" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-2">HOW AIR12 WORKS</div>
          <h2 className="text-2xl sm:text-3xl heading tracking-tight mb-8">Three steps from quote to flight</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="grid-card p-6" data-testid={`step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="grid place-items-center w-10 h-10 bg-[hsl(var(--foreground))] text-white rounded-[2px]">
                    <s.icon size={20} weight="bold" />
                  </span>
                  <span className="overline">0{i + 1}</span>
                </div>
                <h3 className="heading text-lg mt-4 tracking-tight">{s.title}</h3>
                <p className="text-sm text-[hsl(var(--muted))] mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for urgent business cargo — service-proof strip (same card/icon grammar as steps) */}
      <section className="bg-[hsl(var(--surface))] border-b py-14 md:py-20 px-5 md:px-10" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-2">WHY AIR12</div>
          <h2 className="text-2xl sm:text-3xl heading tracking-tight mb-8">Built for urgent business cargo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="grid-card p-6" data-testid={`benefit-${i + 1}`}>
                <span className="grid place-items-center w-10 h-10 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border border-[hsl(var(--success))] rounded-[2px]">
                  <b.icon size={20} weight="bold" />
                </span>
                <h3 className="heading text-lg mt-4 tracking-tight">{b.title}</h3>
                <p className="text-sm text-[hsl(var(--muted))] mt-1.5 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[hsl(var(--muted))] mt-6" data-testid="benefits-disclaimer">
            *Subject to route, location, capacity and operational availability.
          </p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1" data-testid="benefits-disclaimer-sameday">
            **For same-day delivery, book before 12 PM.
          </p>
        </div>
      </section>

      {/* Priority Routes */}
      <section className="relative bg-[hsl(var(--surface))] border-y py-14 md:py-20 px-5 md:px-10 overflow-hidden" data-testid="priority-routes-section">
        <div className="absolute inset-0 divider-grid opacity-[0.35] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-2">AIR12 PRIORITY ROUTES</div>
            <h2 className="text-2xl sm:text-3xl heading tracking-tight">Urgent cargo, moved by air</h2>
            <p className="text-sm text-[hsl(var(--muted))] mt-3 leading-relaxed max-w-xl">
              Air12 operates on Priority Routes where urgent business cargo can be moved by air subject to
              shipment clearance, cargo acceptance and flight availability.
            </p>
            <Link to="/priority-routes" className="btn-ghost mt-6" data-testid="view-priority-routes">
              View Priority Routes <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="flex flex-col gap-5">
            <div className="grid-card grid-card-strong p-6 bg-[hsl(var(--surface))]" data-testid="priority-route-ticket-del">
              <div className="flex items-center justify-between">
                <span className="overline">CURRENTLY ACTIVE</span>
                <span className="status-pill status-success">ACTIVE</span>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <span className="id-chip id-chip-strong text-base px-3 py-1.5">BOM</span>
                <Lightning size={18} weight="fill" className="text-[hsl(var(--accent))]" />
                <span className="id-chip id-chip-strong text-base px-3 py-1.5">DEL</span>
              </div>
              <p className="text-xs text-[hsl(var(--muted))] mt-4 leading-relaxed">
                Mumbai ↔ Delhi · next-suitable-flight movement, subject to clearance and capacity.
              </p>
            </div>
            <div className="grid-card grid-card-strong p-6 bg-[hsl(var(--surface))]" data-testid="priority-route-ticket-blr">
              <div className="flex items-center justify-between">
                <span className="overline">CURRENTLY ACTIVE</span>
                <span className="status-pill status-success">ACTIVE</span>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <span className="id-chip id-chip-strong text-base px-3 py-1.5">BOM</span>
                <Lightning size={18} weight="fill" className="text-[hsl(var(--accent))]" />
                <span className="id-chip id-chip-strong text-base px-3 py-1.5">BLR</span>
              </div>
              <p className="text-xs text-[hsl(var(--muted))] mt-4 leading-relaxed">
                Mumbai ↔ Bengaluru · next-suitable-flight movement, subject to clearance and capacity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
      <CheckPriceDrawer open={priceOpen} onOpenChange={setPriceOpen} interim={interim} />
    </div>
  );
}
